import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";

const POLL_MS = 20000;

function ago(iso) {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  return hours < 24 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`;
}

export const NotificationBell = ({ fetchFeed, markRead, testId = "notifications" }) => {
  const [feed, setFeed] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const load = useCallback(() => fetchFeed().then(setFeed).catch(() => {}), [fetchFeed]);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && feed.unread) {
      await markRead().catch(() => {});
      setFeed((prev) => ({ ...prev, unread: 0 }));
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={toggle} aria-label="Notifications" className="relative w-9 h-9 rounded-full border border-sand hover:border-espresso flex items-center justify-center transition-colors" data-testid={`${testId}-bell`}>
        <Bell size={15} strokeWidth={1.5} className="text-ink/75" />
        {feed.unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-burgundy text-bone text-[10px] leading-[18px] text-center" data-testid={`${testId}-unread-count`}>{feed.unread}</span>}
      </button>
      {open && (
        <div className="fixed left-4 right-4 top-20 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[320px] bg-bone border border-sand/80 rounded-xl shadow-xl z-[95] overflow-hidden" data-testid={`${testId}-panel`}>
          <p className="px-4 py-3 text-[10px] tracking-[0.22em] uppercase text-mocha border-b border-sand/70">Notifications</p>
          {feed.items.length ? (
            <ul className="max-h-[360px] overflow-y-auto divide-y divide-sand/60">
              {feed.items.map((item) => (
                <li key={item.id} className="px-4 py-3" data-testid={`${testId}-item-${item.kind}`}>
                  <div className="flex items-start gap-2">
                    {!item.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-burgundy shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm text-ink font-medium">{item.title}</p>
                      <p className="mt-0.5 text-xs text-ink/60 leading-relaxed">{item.body}</p>
                      <p className="mt-1 text-[10px] text-ink/40">{ago(item.created_at)}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : <p className="px-4 py-6 text-sm text-ink/50 text-center" data-testid={`${testId}-empty`}>Nothing yet. Bookings, refunds and payouts will show up here.</p>}
        </div>
      )}
    </div>
  );
};
